import { useRef, useState, type PointerEvent } from "react";
import type { Movement } from "../movement";

/** Two touches this close together are a double tap. */
export const DOUBLE_TAP_MS = 320;
/** Inside this share of the stick's radius is its middle: no direction (turbo, in stick-turbo). */
const MIDDLE = 0.28;
/** The knob travels this share of the radius; a finger that far out is full deflection. */
const REACH = 0.6;

/**
 * One on-screen movement control, the same for every console: a D-pad of
 * four buttons, a drag stick whose middle is turbo, or a plain stick
 * (movement.ts). It reports where it points as x and y from -1 to 1 (right
 * and down positive): the D-pad and a stick past its middle report whole
 * steps or how far out the finger is; the caller makes buttons or an
 * analog stick of that. Turbo in the middle is held while the finger stays
 * there; a double tap there locks it.
 */
export default function MoveControl({
  mode,
  active,
  turboLocked,
  onTurbo,
  onTurboLock,
  onMove,
  label,
  className = "",
}: {
  mode: Movement;
  active: boolean;
  turboLocked: boolean;
  onTurbo: (down: boolean) => void;
  onTurboLock: () => void;
  onMove: (x: number, y: number) => void;
  label: string;
  className?: string;
}) {
  const lastTap = useRef(0);
  const turbo = useRef(false);
  const box = useRef<HTMLDivElement>(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const held = useRef({ up: false, down: false, left: false, right: false });

  const setTurbo = (on: boolean) => {
    if (mode !== "stick-turbo") on = false;
    if (turbo.current === on) return;
    turbo.current = on;
    onTurbo(on);
  };

  // The D-pad: each button held adds its step.
  const step = (dir: keyof typeof held.current, down: boolean) => {
    held.current = { ...held.current, [dir]: down };
    const h = held.current;
    onMove(Number(h.right) - Number(h.left), Number(h.down) - Number(h.up));
  };
  const dpadButton = (dir: keyof typeof held.current, glyph: string) => (
    <button
      type="button"
      className={`pad ${dir}`}
      aria-label={`${label} ${dir}`}
      onPointerDown={(e) => {
        e.preventDefault();
        if (!active) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        step(dir, true);
      }}
      onPointerUp={() => step(dir, false)}
      onPointerCancel={() => step(dir, false)}
      onLostPointerCapture={() => step(dir, false)}
      onContextMenu={(e) => e.preventDefault()}
    >
      {glyph}
    </button>
  );

  const drag = (e: PointerEvent<HTMLDivElement>, first: boolean) => {
    const rect = box.current!.getBoundingClientRect();
    const radius = rect.width / 2;
    const dx = e.clientX - (rect.left + radius);
    const dy = e.clientY - (rect.top + rect.height / 2);
    const length = Math.hypot(dx, dy);
    const middle = length <= radius * MIDDLE;
    if (first && middle && mode === "stick-turbo") {
      const now = Date.now();
      const double = now - lastTap.current < DOUBLE_TAP_MS;
      lastTap.current = double ? 0 : now;
      if (double) onTurboLock();
    }
    const reach = radius * REACH;
    const scale = middle ? 0 : Math.min(1, length / reach) / (length || 1);
    onMove(dx * scale, dy * scale);
    setTurbo(middle);
    const shown = Math.min(length, reach) / (length || 1);
    setKnob({ x: dx * shown, y: dy * shown });
  };

  const letGo = () => {
    onMove(0, 0);
    setTurbo(false);
    setKnob({ x: 0, y: 0 });
  };

  if (mode === "dpad") {
    return (
      <div className={`dpad ${className}`} aria-label={label}>
        {dpadButton("up", "▲")}
        {dpadButton("left", "◀")}
        {dpadButton("right", "▶")}
        {dpadButton("down", "▼")}
      </div>
    );
  }
  return (
    <div
      ref={box}
      className={`dragbox ${className}`}
      aria-label={label}
      onPointerDown={(e) => {
        e.preventDefault();
        if (!active) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        drag(e, true);
      }}
      onPointerMove={(e) => e.currentTarget.hasPointerCapture(e.pointerId) && drag(e, false)}
      onPointerUp={letGo}
      onPointerCancel={letGo}
      onLostPointerCapture={letGo}
      onContextMenu={(e) => e.preventDefault()}
    >
      {mode === "stick-turbo" && <span className={`dragbox-ring${turboLocked ? " locked" : ""}`} />}
      <span className="dragbox-knob" style={{ transform: `translate(${knob.x}px, ${knob.y}px)` }} />
    </div>
  );
}
